import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Mail, CheckCircle2 } from 'lucide-react';
import { submitContactInquiry } from '../../redux/contactInquirySlice';

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

// Contact-sales modal for a custom (non-purchasable) plan. Works on the doctor
// page and the public landing page. On submit it records a contact inquiry and
// shows a thank-you message.
const ContactModal = ({ pkg, source = 'landing', doctorId = null, defaultName = '', defaultEmail = '', onClose }) => {
  const dispatch = useDispatch();
  const submitting = useSelector((s) => s.contactInquiry.submitting);
  const [form, setForm] = useState({ name: defaultName, email: defaultEmail, notes: '' });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const valid = form.name.trim() && isEmail(form.email);

  const submit = async () => {
    if (!valid) { setErr('Please enter your name and a valid email.'); return; }
    setErr('');
    try {
      await dispatch(submitContactInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        notes: form.notes.trim(),
        packageId: pkg?.id || null,
        packageName: pkg?.name || null,
        doctorId,
        source,
      })).unwrap();
      setDone(true);
    } catch (e) {
      setErr(typeof e === 'string' ? e : 'Could not send your request — please try again.');
    }
  };

  const field = 'w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] px-4" data-testid="contact-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#4ABDBD]" />
            <h2 className="text-lg font-bold text-[#1a365d]">Contact us{pkg?.name ? ` — ${pkg.name}` : ''}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 flex flex-col items-center text-center gap-3" data-testid="contact-success">
            <span className="p-3 rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="w-8 h-8" /></span>
            <h3 className="text-lg font-bold text-[#1a365d]">Thank you for contacting us</h3>
            <p className="text-sm text-gray-600">
              Our Therapeu Time team will reach out to <strong>{form.email}</strong> shortly to discuss this plan.
            </p>
            <button type="button" onClick={onClose} className="mt-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold text-sm hover:shadow-lg transition-all">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-slate-500">
                Tell us a little about your needs and we&apos;ll get back to you about <strong>{pkg?.name || 'a custom plan'}</strong>.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Name</label>
                <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Your name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Additional notes</label>
                <textarea data-testid="contact-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={field} placeholder="What are you looking for? (team size, hospital, requirements…)" />
              </div>
              {err && <p className="text-xs text-red-600" data-testid="contact-error">{err}</p>}
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={submit}
                disabled={!valid || submitting}
                data-testid="contact-submit"
                className={`w-full px-4 py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 ${(!valid || submitting) ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg'}`}
              >
                {submitting ? (<><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>) : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
